// Checkout.jsx — Hé by SiewLi one-page checkout (web, responsive)
// Converted from the "Hé Checkout Redesign" canvas.
//
// Usage:
//   import Checkout from './Checkout';
//   <Checkout balance={660} />
//
// Fonts: load DM Sans + Playfair Display once in your app, e.g. in index.html:
//   <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap" rel="stylesheet">
//
// Placeholders to replace before shipping: [X], [X–Y], [NAME], [PHONE],
// [ADDRESS LINE], [POSTAL CODE], [RATE], and the poster image.
// SGD_RATE below is a stand-in for layout only — use your provider's rate
// or a fixed SGD price list.

import React, { useState } from 'react';

const C = {
  bg: '#0A081C',
  card: '#120F2E',
  gold: '#E3CC86',
  goldLight: '#F3E2AE',
  text: '#ECE8F7',
  muted: '#B3ADC9',
  ink: '#17122F',
  line: 'rgba(227,204,134,0.18)',
  lineSoft: 'rgba(227,204,134,0.15)',
  lineMid: 'rgba(227,204,134,0.35)',
  lineOff: 'rgba(227,204,134,0.25)',
  selBg: 'rgba(227,204,134,0.10)',
};

const PRODUCT = {
  name: 'God of Wealth Poster 财神爷海报',
  variant: 'A2 print',
  usd: 50,
  gems: 500,
};

const STORES = [
  { id: 'sg', name: 'Singapore · Shaw House', addr: '350 Orchard Road, #18-09/10, S238868' },
  { id: 'batam', name: 'Batam · Sansan', addr: 'Menara Aria, Harbour Bay Office Tower, 13th Floor, Riau Islands' },
];

const GEMS_PER_USD = 10;
const DELIVERY_FEE_USD = 25;
const FREE_DELIVERY_FROM_USD = 500;
const SGD_RATE = 1.3; // stand-in
const MAX_QTY = 12;

const fmt = (n) => n.toLocaleString('en-US');

// ---------- small building blocks ----------

const sectionStyle = {
  background: C.card,
  border: `1px solid ${C.line}`,
  borderRadius: 18,
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const eyebrow = {
  margin: 0,
  fontSize: 13,
  color: C.muted,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  fontWeight: 500,
};

const outlineBtn = {
  minHeight: 44,
  padding: '0 16px',
  background: 'transparent',
  border: `1px solid ${C.lineMid}`,
  borderRadius: 22,
  color: C.gold,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

function Radio({ on }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 20,
        height: 20,
        borderRadius: 10,
        border: `2px solid ${on ? C.gold : 'rgba(227,204,134,0.45)'}`,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: '0 0 auto',
      }}
    >
      {on && <span style={{ width: 8, height: 8, borderRadius: 4, background: C.gold }} />}
    </span>
  );
}

function FreeBadge() {
  return (
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.06em',
        color: C.ink,
        background: C.gold,
        borderRadius: 10,
        padding: '4px 10px',
      }}
    >
      FREE
    </span>
  );
}

const PinIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: '0 0 auto', marginTop: 2 }}>
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const TruckIcon = ({ size = 22, color = C.gold }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: '0 0 auto' }}>
    <path d="M3 6h11v10H3z" />
    <path d="M14 9h4l3 3v4h-7" />
    <circle cx="7" cy="17.5" r="1.8" />
    <circle cx="17" cy="17.5" r="1.8" />
  </svg>
);

const GemIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 3h12l4 6-10 12L2 9z" />
    <path d="M2 9h20" />
    <path d="M12 21L8 9l4-6 4 6-4 12" />
  </svg>
);

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5" />
    <path d="M12 19l-7-7 7-7" />
  </svg>
);

// ---------- main component ----------

export default function Checkout({
  balance = 660,
  onBack,
  onTopUp,
  onPlaceOrder,
  termsHref = '#',
}) {
  const [qty, setQty] = useState(1);
  const [pay, setPay] = useState('gems'); // 'gems' | 'card'
  const [store, setStore] = useState('sg');
  const [storeOpen, setStoreOpen] = useState(false);
  const [fulfil, setFulfil] = useState('pickup'); // 'pickup' | 'delivery'
  const [currency, setCurrency] = useState('USD'); // 'USD' | 'SGD'
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucher, setVoucher] = useState('');

  // --- pricing ---
  const itemsUsd = PRODUCT.usd * qty;
  const itemsGems = PRODUCT.gems * qty;
  const isDelivery = fulfil === 'delivery';
  const feeUsd = isDelivery && itemsUsd < FREE_DELIVERY_FROM_USD ? DELIVERY_FEE_USD : 0;
  const totalUsd = itemsUsd + feeUsd;
  const totalGems = itemsGems + feeUsd * GEMS_PER_USD;

  const enoughGems = balance >= totalGems;
  const method = enoughGems ? pay : 'card';
  const isCard = method === 'card';
  const sgd = currency === 'SGD';

  const money = (usd) => (sgd ? `S$${(usd * SGD_RATE).toFixed(2)}` : `US$${usd.toFixed(2)}`);
  const gems = (n) => `${fmt(n)} Gems`;

  const subtotalLabel = isCard ? money(itemsUsd) : gems(itemsGems);
  const feeLabel = feeUsd ? (isCard ? money(feeUsd) : gems(feeUsd * GEMS_PER_USD)) : 'Free';
  const totalLabel = isCard ? money(totalUsd) : gems(totalGems);
  const payLabel = `Pay ${totalLabel}`;

  const currentStore = STORES.find((s) => s.id === store);

  const handlePlaceOrder = () => {
    onPlaceOrder?.({
      qty,
      method,
      currency: isCard ? currency : null,
      fulfilment: fulfil,
      storeId: fulfil === 'pickup' ? store : null,
      voucher: voucher || null,
      totalUsd,
      totalGems,
    });
  };

  const optionStyle = (on) => ({
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 16,
    borderRadius: 14,
    background: on ? C.selBg : 'transparent',
    border: `1px solid ${on ? C.gold : C.lineOff}`,
    color: C.text,
    cursor: 'pointer',
    fontFamily: 'inherit',
  });

  const segBtn = (on) => ({
    minWidth: 56,
    minHeight: 34,
    borderRadius: 17,
    border: 0,
    background: on ? C.gold : 'transparent',
    color: on ? C.ink : C.gold,
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  });

  return (
    <div
      style={{
        minHeight: '100vh',
        background: C.bg,
        color: C.text,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        padding: '40px 24px 64px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: 1080, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 28 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <button
            type="button"
            aria-label="Back to product"
            onClick={onBack}
            style={{ ...outlineBtn, width: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <BackIcon />
          </button>
          <h1 style={{ margin: 0, fontFamily: "'Playfair Display', Georgia, serif", fontSize: 36, fontWeight: 700, color: C.gold }}>
            Checkout
          </h1>
          <button
            type="button"
            aria-label={`Gem balance ${balance}. Top up Gems`}
            onClick={onTopUp}
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minHeight: 44,
              padding: '0 8px 0 16px',
              borderRadius: 22,
              border: `1px solid ${C.lineMid}`,
              background: 'transparent',
              color: C.gold,
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <GemIcon />
            <span style={{ color: C.text }}>{fmt(balance)} Gems</span>
            <span style={{ minHeight: 30, padding: '0 12px', borderRadius: 15, background: C.gold, color: C.ink, display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 700 }}>
              Top up
            </span>
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
          {/* Left column */}
          <div style={{ flex: '999 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Item */}
            <section style={{ ...sectionStyle, flexDirection: 'row', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
              <div
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: 12,
                  background: '#F4F1E8',
                  color: '#5B5470',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  flex: '0 0 auto',
                }}
              >
                {/* Replace with <img src={...} alt={PRODUCT.name} /> */}
                Poster image
              </div>
              <div style={{ flex: '1 1 240px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 18, fontWeight: 600, color: C.goldLight }}>{PRODUCT.name}</div>
                <div style={{ fontSize: 14, color: C.muted }}>{PRODUCT.variant}</div>
                <div style={{ fontSize: 15, fontWeight: 500 }}>
                  US${PRODUCT.usd.toFixed(2)} or {fmt(PRODUCT.gems)} Gems
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${C.lineMid}`, borderRadius: 12 }}>
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  style={{ width: 44, height: 44, background: 'transparent', border: 0, color: C.gold, fontSize: 20, cursor: 'pointer' }}
                >
                  −
                </button>
                <span aria-live="polite" style={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>
                  {qty}
                </span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  onClick={() => setQty((q) => Math.min(MAX_QTY, q + 1))}
                  style={{ width: 44, height: 44, background: 'transparent', border: 0, color: C.gold, fontSize: 20, cursor: 'pointer' }}
                >
                  +
                </button>
              </div>
            </section>

            {/* Fulfilment: pick up (primary) / delivery (secondary) */}
            <section style={sectionStyle}>
              <h2 style={eyebrow}>How to get it</h2>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  padding: 18,
                  borderRadius: 14,
                  background: !isDelivery ? C.selBg : 'transparent',
                  border: `1px solid ${!isDelivery ? C.gold : C.lineMid}`,
                }}
              >
                <button
                  type="button"
                  aria-pressed={!isDelivery}
                  onClick={() => setFulfil('pickup')}
                  style={{
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: 0,
                    minHeight: 44,
                    background: 'transparent',
                    border: 0,
                    color: C.text,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <Radio on={!isDelivery} />
                  <span style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 18, fontWeight: 600 }}>Pick up in store</span>
                    <span style={{ fontSize: 13, color: C.muted }}>Singapore or Batam · ready in [X] days · we'll message you</span>
                  </span>
                  <FreeBadge />
                </button>

                {!isDelivery && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingLeft: 34 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <PinIcon />
                      <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ ...eyebrow }}>Pick up at</div>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>{currentStore.name}</div>
                        <div style={{ fontSize: 14, color: C.muted }}>{currentStore.addr}</div>
                      </div>
                      <button type="button" style={outlineBtn} onClick={() => setStoreOpen((o) => !o)}>
                        {storeOpen ? 'Done' : 'Change'}
                      </button>
                    </div>

                    {storeOpen && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: `1px solid ${C.lineSoft}`, paddingTop: 16 }}>
                        {STORES.map((s) => {
                          const on = s.id === store;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              aria-pressed={on}
                              onClick={() => {
                                setStore(s.id);
                                setStoreOpen(false);
                              }}
                              style={{
                                textAlign: 'left',
                                display: 'flex',
                                gap: 12,
                                alignItems: 'center',
                                padding: '14px 16px',
                                borderRadius: 14,
                                background: on ? C.selBg : 'transparent',
                                border: `1px solid ${on ? C.gold : C.lineOff}`,
                                color: C.text,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                              }}
                            >
                              <Radio on={on} />
                              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <span style={{ fontSize: 15, fontWeight: 600 }}>{s.name}</span>
                                <span style={{ fontSize: 13, color: C.muted }}>{s.addr}</span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!isDelivery ? (
                <button
                  type="button"
                  onClick={() => {
                    setFulfil('delivery');
                    setStoreOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minHeight: 44,
                    padding: '0 4px',
                    background: 'transparent',
                    border: 0,
                    color: C.muted,
                    fontSize: 14,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  <TruckIcon size={18} color="currentColor" />
                  <span style={{ flex: '1 1 auto' }}>
                    Prefer delivery? US${DELIVERY_FEE_USD} (free from US${FREE_DELIVERY_FROM_USD}) · Singapore only
                  </span>
                  <span style={{ color: C.gold, fontWeight: 600 }}>Choose</span>
                </button>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 14,
                    padding: 18,
                    borderRadius: 14,
                    background: C.selBg,
                    border: `1px solid ${C.gold}`,
                  }}
                >
                  <TruckIcon />
                  <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={eyebrow}>Delivery · {feeLabel} · deliver to</div>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>[NAME] · [PHONE]</div>
                    <div style={{ fontSize: 14, color: C.muted }}>[ADDRESS LINE], Singapore [POSTAL CODE]</div>
                    <div style={{ fontSize: 14, color: C.muted }}>Arrives in [X–Y] working days</div>
                    <div style={{ fontSize: 14, color: C.muted }}>Singapore addresses only</div>
                  </div>
                  <button type="button" style={outlineBtn}>
                    Change
                  </button>
                </div>
              )}
            </section>

            {/* Payment method */}
            <section style={{ ...sectionStyle, gap: 12 }}>
              <h2 style={eyebrow}>Pay with</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                <button
                  type="button"
                  aria-pressed={method === 'gems'}
                  disabled={!enoughGems}
                  onClick={() => setPay('gems')}
                  style={{ ...optionStyle(method === 'gems'), opacity: enoughGems ? 1 : 0.55, cursor: enoughGems ? 'pointer' : 'not-allowed' }}
                >
                  <span style={{ fontSize: 16, fontWeight: 600 }}>Gems</span>
                  <span style={{ fontSize: 13, color: C.muted }}>
                    {enoughGems
                      ? `Balance ${fmt(balance)} → ${fmt(balance - totalGems)} after`
                      : `Balance ${fmt(balance)} · need ${fmt(totalGems)}`}
                  </span>
                </button>
                <button type="button" aria-pressed={isCard} onClick={() => setPay('card')} style={optionStyle(isCard)}>
                  <span style={{ fontSize: 16, fontWeight: 600 }}>Card</span>
                  <span style={{ fontSize: 13, color: C.muted }}>{money(totalUsd)}</span>
                </button>
              </div>
              {!enoughGems && (
                <div style={{ fontSize: 13, color: C.muted }}>
                  Not enough Gems for this order.{' '}
                  <button
                    type="button"
                    onClick={onTopUp}
                    style={{ background: 'none', border: 0, padding: 0, color: C.gold, textDecoration: 'underline', cursor: 'pointer', font: 'inherit' }}
                  >
                    Top up Gems
                  </button>
                </div>
              )}
            </section>
          </div>

          {/* Order summary */}
          <aside style={{ ...sectionStyle, flex: '1 1 320px', minWidth: 0, gap: 18 }}>
            <h2 style={{ margin: 0, fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, color: C.gold }}>Order summary</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 15 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: C.muted }}>{qty === 1 ? '1 item' : `${qty} items`}</span>
                <span>{subtotalLabel}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: C.muted }}>{isDelivery ? 'Delivery' : 'Pickup'}</span>
                <span>{feeLabel}</span>
              </div>
            </div>

            {voucherOpen ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label htmlFor="voucher" style={{ fontSize: 13, color: C.muted }}>
                    Voucher code
                  </label>
                  <input
                    id="voucher"
                    type="text"
                    value={voucher}
                    onChange={(e) => setVoucher(e.target.value)}
                    placeholder="e.g. WELCOME10"
                    style={{
                      height: 44,
                      padding: '0 14px',
                      borderRadius: 12,
                      border: `1px solid ${C.lineMid}`,
                      background: C.bg,
                      color: C.text,
                      fontSize: 15,
                      fontFamily: 'inherit',
                      boxSizing: 'border-box',
                      width: '100%',
                    }}
                  />
                </div>
                <button type="button" style={{ ...outlineBtn, borderRadius: 12, border: `1px solid ${C.gold}` }}>
                  Apply
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setVoucherOpen(true)}
                style={{
                  alignSelf: 'flex-start',
                  minHeight: 44,
                  padding: 0,
                  background: 'transparent',
                  border: 0,
                  color: C.gold,
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Have a voucher?
              </button>
            )}

            {/* Total + currency switch (card only) */}
            <div style={{ borderTop: `1px solid ${C.lineSoft}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, fontWeight: 600 }}>Total</span>
                {isCard && (
                  <div role="group" aria-label="Currency" style={{ display: 'flex', padding: 3, borderRadius: 20, border: `1px solid ${C.lineMid}` }}>
                    <button type="button" aria-pressed={!sgd} onClick={() => setCurrency('USD')} style={segBtn(!sgd)}>
                      USD
                    </button>
                    <button type="button" aria-pressed={sgd} onClick={() => setCurrency('SGD')} style={segBtn(sgd)}>
                      SGD
                    </button>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.goldLight, textAlign: 'right' }}>{totalLabel}</div>
              {isCard && (
                <div style={{ fontSize: 13, lineHeight: 1.5, color: C.muted, textAlign: 'right' }}>
                  {sgd
                    ? 'Charged in SGD at [RATE], locked when you pay. No foreign-currency fee from your bank.'
                    : 'Charged in USD. Your bank may add a foreign-currency fee — switch to SGD to avoid it.'}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handlePlaceOrder}
              style={{
                minHeight: 56,
                borderRadius: 28,
                border: 0,
                background: C.gold,
                color: C.ink,
                fontSize: 18,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {payLabel}
            </button>
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: C.muted, textAlign: 'center' }}>
              By placing this order you agree to the{' '}
              <a href={termsHref} style={{ color: C.gold }}>
                Terms &amp; Conditions
              </a>
              .
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}
